import { useState } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

import { auth } from "../firebase";
import API from "../api";

import { useNavigate } from "react-router-dom";

import { motion } from "framer-motion";

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowLeft,
} from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  // =====================================================
  // 🔐 EMAIL LOGIN
  // =====================================================

  const handleLogin = async () => {
    if (loading) return;

    if (!email || !password) {
      alert("Please enter your credentials.");
      return;
    }

    try {
      setLoading(true);

      // 🔥 LOGIN
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

      const user = userCredential.user;

      // =====================================================
      // ✅ EMAIL VERIFICATION CHECK
      // =====================================================

      if (!user.emailVerified) {
        alert("Please verify your email first.");
        await signOut(auth);
        return;
      }

      // =====================================================
      // 🔥 FORCE TOKEN REFRESH
      // =====================================================

      const token = await user.getIdToken(true);

      // =====================================================
      // 🔥 VERIFY USER FROM BACKEND
      // =====================================================

      const res = await API.post(
        "verify-token/",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;

      // =====================================================
      // 🔥 CHECK ADMIN ACCESS
      // =====================================================

      const isAdmin =
        data.isSuperAdmin ||
        data.organizations?.some(
          (org) => org.role === "admin"
        );

      if (!isAdmin) {
        await signOut(auth);

        alert(
          "Unauthorized Admin Access"
        );

        return;
      }

      // =====================================================
      // 🔥 STORE DATA
      // =====================================================

      if (data.organizations?.length > 0) {
        const primaryOrg =
          data.organizations[0];

        localStorage.setItem(
          "orgId",
          primaryOrg.orgId || ""
        );

        localStorage.setItem(
          "orgSlug",
          primaryOrg.orgSlug || ""
        );

        localStorage.setItem(
          "role",
          primaryOrg.role || ""
        );
      }

      // =====================================================
      // ✅ SUCCESS
      // =====================================================

      navigate("/redirect");

    } catch (error) {
      console.error(error);

      alert(
        error.response?.data?.error ||
          error.message ||
          "Login failed."
      );

      await signOut(auth);

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // 🔐 GOOGLE LOGIN
  // =====================================================

  const handleGoogleLogin = async () => {
    if (loading) return;

    try {
      setLoading(true);

      const provider =
        new GoogleAuthProvider();

      const result =
        await signInWithPopup(
          auth,
          provider
        );

      const user = result.user;

      // 🔥 GET TOKEN
      const token =
        await user.getIdToken(true);

      // 🔥 VERIFY
      const res = await API.post(
        "verify-token/",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;

      // 🔥 ADMIN CHECK
      const isAdmin =
        data.isSuperAdmin ||
        data.organizations?.some(
          (org) => org.role === "admin"
        );

      if (!isAdmin) {
        await signOut(auth);

        alert(
          "Unauthorized Admin Access"
        );

        return;
      }

      // 🔥 STORE
      if (data.organizations?.length > 0) {
        const primaryOrg =
          data.organizations[0];

        localStorage.setItem(
          "orgId",
          primaryOrg.orgId || ""
        );

        localStorage.setItem(
          "orgSlug",
          primaryOrg.orgSlug || ""
        );

        localStorage.setItem(
          "role",
          primaryOrg.role || ""
        );
      }

      navigate("/redirect");

    } catch (error) {
      console.error(error);

      alert(
        error.response?.data?.error ||
          error.message ||
          "Google login failed."
      );

      await signOut(auth);

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // 🔐 FORGOT PASSWORD
  // =====================================================

  const handleForgotPassword =
    async () => {
      if (!email) {
        alert(
          "Please enter your registered email."
        );
        return;
      }

      try {
        await sendPasswordResetEmail(
          auth,
          email
        );

        alert(
          "If this email exists, a password reset link has been sent."
        );

      } catch (error) {
        console.error(error);

        alert(
          "If this email exists, a password reset link has been sent."
        );
      }
    };

  // =====================================================
  // 🎨 UI
  // =====================================================

  return (
    <div className="min-h-screen flex bg-white font-sans text-slate-900 overflow-hidden">

      {/* LEFT SIDE */}

      <div className="hidden lg:flex w-[45%] bg-gradient-to-br from-indigo-900 via-slate-900 to-black flex-col justify-between p-16 text-white relative">

        <div className="absolute top-0 right-0 w-full h-1/2 bg-indigo-500/10 blur-[120px]" />

        <div className="relative z-10">

          <div className="flex items-center mb-20">
            <div className="bg-white p-1.5 rounded shadow-lg mr-4">
              <img
                src="/logo.png"
                alt="Logo"
                className="w-6 h-6"
              />
            </div>

            <h1 className="text-2xl font-bold tracking-tight">
              Avaami
              <span className="text-indigo-400 ml-1">
                Pulse
              </span>
            </h1>
          </div>

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
            }}
          >
            <h2 className="text-6xl font-black leading-[1.1] mb-6">
              Administrative
              <br />

              <span className="text-indigo-400 underline decoration-indigo-500/30">
                Control Portal
              </span>
            </h2>

            <div className="w-12 h-1.5 bg-indigo-500 rounded-full mb-8" />

            <p className="text-slate-400 text-lg max-w-sm">
              Secure access for authorized administrators only.
            </p>
          </motion.div>
        </div>

        <div className="relative z-10 flex gap-12 border-t border-white/10 pt-10">

          <div>
            <span className="text-indigo-400 text-[10px] uppercase block mb-1">
              Compliance
            </span>

            <span className="text-slate-300 text-xs">
              ISO 27001 Certified
            </span>
          </div>

          <div>
            <span className="text-indigo-400 text-[10px] uppercase block mb-1">
              System Status
            </span>

            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />

              <span className="text-slate-300 text-xs">
                Operational
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}

      <div className="w-full lg:w-[55%] flex justify-center items-center p-8 bg-slate-50 md:bg-white">

        <div className="w-full max-w-md">

          <div className="mb-10">
            <h3 className="text-4xl font-black mb-2">
              SIGN IN
            </h3>

            <p className="text-slate-500">
              Enter your administrative credentials to continue.
            </p>
          </div>

          <div className="space-y-5">

            {/* EMAIL */}

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">
                Email Address
              </label>

              <div className="relative mt-2">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />

                <input
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(
                      e.target.value
                    )
                  }
                  placeholder="admin@company.com"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none"
                />
              </div>
            </div>

            {/* PASSWORD */}

            <div>
              <div className="flex justify-between">

                <label className="text-xs font-bold text-slate-500 uppercase">
                  Password
                </label>

                <button
                  onClick={
                    handleForgotPassword
                  }
                  className="text-xs text-indigo-600 font-bold"
                >
                  Forgot Password?
                </button>
              </div>

              <div className="relative mt-2">
                <Lock
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={18}
                />

                <input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  value={password}
                  onChange={(e) =>
                    setPassword(
                      e.target.value
                    )
                  }
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-4 bg-slate-50 border rounded-xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none"
                />

                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  onClick={() =>
                    setShowPassword(
                      !showPassword
                    )
                  }
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            {/* LOGIN */}

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-indigo-600 transition disabled:bg-slate-300"
            >
              {loading
                ? "Verifying..."
                : "Access Dashboard"}
            </button>

            {/* DIVIDER */}

            <div className="text-center text-xs text-slate-400 uppercase">
              Or continue with
            </div>

            {/* GOOGLE */}

            <button
              onClick={
                handleGoogleLogin
              }
              disabled={loading}
              className="w-full border py-4 rounded-xl flex justify-center gap-3 font-bold hover:bg-slate-50"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                className="w-5"
                alt="Google"
              />

              Sign in with Google
            </button>
          </div>

          {/* BACK */}

          <div className="mt-12 text-center">
            <button
              onClick={() =>
                navigate("/")
              }
              className="text-sm text-slate-400 hover:text-slate-900 flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}